/**
 * Pure drafts for issuing a job PO from a designer-invoice review.
 * HITL approve (final gate) is the authorization — one PO per job + plBucket.
 * Payment method comes from the designerInvoices queue record.
 * external_design → plBucket=design; external_engineering / other → plBucket=engineering.
 */

import type { PurchaseApprovalStep, PurchaseAttachment, PurchaseCostObject, PurchaseJobPlBucket, PurchaseLineItem } from '../types/purchasing';
import type { DesignerInvoiceLine } from './designerInvoiceGates';
import {
  DESIGNER_INVOICE_PAYMENT_METHOD_LABELS,
  type DesignerInvoicePaymentMethodId,
} from './designerInvoicePayment';
import { roundMoney } from './purchasingLineItems';
import { buildPurchaseCostObject } from './purchasingSpendClass';

/** @deprecated Prefer resolveDesignerInvoicePaymentMethodId. */
export const DESIGNER_INVOICE_PO_PAYMENT_METHOD_ID: DesignerInvoicePaymentMethodId = 'ach_office';
/** @deprecated Prefer DESIGNER_INVOICE_PAYMENT_METHOD_LABELS. */
export const DESIGNER_INVOICE_PO_PAYMENT_METHOD =
  DESIGNER_INVOICE_PAYMENT_METHOD_LABELS.ach_office;

/** Buckets that create a CRM purchaseRequisition on approve (job_direct only). */
export const DESIGNER_INVOICE_PO_BUCKETS = [
  'external_design',
  'external_engineering',
  'other',
] as const;

export type DesignerInvoicePoBucket = (typeof DESIGNER_INVOICE_PO_BUCKETS)[number];

export function isDesignerInvoicePoBucket(bucket: string | null | undefined): bucket is DesignerInvoicePoBucket {
  return (DESIGNER_INVOICE_PO_BUCKETS as readonly string[]).includes(String(bucket || ''));
}

/** Map allocation bucket → purchase / Project P&L node. */
export function designerInvoicePoPlBucket(
  bucket: string | null | undefined,
): PurchaseJobPlBucket {
  if (bucket === 'external_design') return 'design';
  // external_engineering, and legacy/compat `other` (FTET skill) → engineering
  return 'engineering';
}

export function isDesignerInvoiceJobPoLine(line: DesignerInvoiceLine): boolean {
  if (line.phase === 'overhead') return false;
  const bucket = line.bucket || 'external_design';
  if (!isDesignerInvoicePoBucket(bucket)) return false;
  if (!(line.dealId || '').trim()) return false;
  return (Number(line.amount) || 0) > 0;
}

export function invoiceNeedsJobPurchaseOrders(lines: DesignerInvoiceLine[]): boolean {
  return lines.some(isDesignerInvoiceJobPoLine);
}

export function designerInvoiceJobPoGroupKey(line: Pick<DesignerInvoiceLine, 'dealId' | 'projectId'>): string {
  const dealId = (line.dealId || '').trim();
  const projectId = (line.projectId || '').trim() || dealId;
  return `${dealId}::${projectId}`;
}

export type DesignerInvoiceJobPoGroup = {
  key: string;
  dealId: string;
  projectId: string;
  dealLabel: string;
  projectLabel: string;
  lines: DesignerInvoiceLine[];
  amount: number;
  plBucket: PurchaseJobPlBucket;
};

export function groupDesignerInvoiceJobPoLines(lines: DesignerInvoiceLine[]): DesignerInvoiceJobPoGroup[] {
  const byKey = new Map<string, DesignerInvoiceJobPoGroup>();
  for (const line of lines) {
    if (!isDesignerInvoiceJobPoLine(line)) continue;
    const dealId = (line.dealId || '').trim();
    const projectId = (line.projectId || '').trim() || dealId;
    const plBucket = designerInvoicePoPlBucket(line.bucket);
    // One PO per job + plBucket so design and engineering on the same job stay separate.
    const key = `${dealId}::${projectId}::${plBucket}`;
    const existing = byKey.get(key);
    const amount = roundMoney(Number(line.amount) || 0);
    if (existing) {
      existing.lines.push(line);
      existing.amount = roundMoney(existing.amount + amount);
      continue;
    }
    byKey.set(key, {
      key,
      dealId,
      projectId,
      dealLabel: (line.dealLabel || '').trim() || dealId,
      projectLabel: (line.projectLabel || '').trim() || projectId,
      lines: [line],
      amount,
      plBucket,
    });
  }
  return Array.from(byKey.values());
}

function lineDescription(line: DesignerInvoiceLine, fallback: string): string {
  const desc = (line.description || '').trim() || fallback;
  const hours = Number(line.hours);
  if (Number.isFinite(hours) && hours > 0) {
    const hoursLabel = Number.isInteger(hours) ? String(hours) : String(hours);
    return `${desc} (${hoursLabel}h)`;
  }
  return desc;
}

export function purchaseLineItemsFromDesignerInvoiceLines(
  lines: DesignerInvoiceLine[],
  fallbackDescription: string,
): PurchaseLineItem[] {
  return lines
    .filter(line => (Number(line.amount) || 0) > 0)
    .map((line, index) => {
      const hours = Number(line.hours);
      return {
        id: line.id?.trim() || `line-${index + 1}`,
        description: lineDescription(line, fallbackDescription),
        quantity: 1,
        unitPrice: roundMoney(Number(line.amount) || 0),
        taxable: false,
        ...(Number.isFinite(hours) && hours > 0 ? { hours } : {}),
      };
    });
}

/** HITL approve is the authorization — never put the COO on this step. */
export function designerInvoiceSelfIssueStep(actor: {
  uid: string;
  name: string;
}): PurchaseApprovalStep {
  return {
    gate: 'controller',
    roleLabel: 'Self-authorized',
    userId: actor.uid,
    userName: actor.name.trim() || 'Staff',
    status: 'pending',
  };
}

export function designerInvoiceQuoteAttachment(input: {
  fileName?: string | null;
  storagePath?: string | null;
  contentType?: string | null;
  downloadUrl?: string | null;
}): PurchaseAttachment | null {
  const storagePath = (input.storagePath || '').trim();
  if (!storagePath) return null;
  return {
    name: (input.fileName || '').trim() || 'invoice.pdf',
    storagePath,
    contentType: (input.contentType || '').trim() || 'application/pdf',
    url: (input.downloadUrl || '').trim() || undefined,
    kind: 'quote',
  };
}

export function buildDesignerInvoiceJobPurchaseDraft(input: {
  vendorName: string;
  invoiceNumber: string;
  invoiceDate?: string | null;
  group: DesignerInvoiceJobPoGroup;
}): {
  costObject: PurchaseCostObject;
  lineItems: PurchaseLineItem[];
  justification: string;
  title: string;
} {
  const vendor = input.vendorName.trim() || 'Designer';
  const invoiceNo = input.invoiceNumber.trim() || 'invoice';
  const fallback = `${vendor} ${invoiceNo}`.trim();
  const lineItems = purchaseLineItemsFromDesignerInvoiceLines(input.group.lines, fallback);
  const title = lineItems[0]?.description || `${vendor} — ${invoiceNo}`;
  const dateBit = (input.invoiceDate || '').trim();
  const kindLabel = input.group.plBucket === 'engineering' ? 'Engineering' : 'Designer';
  const justification = [
    `${kindLabel} invoice ${invoiceNo}`,
    dateBit ? `dated ${dateBit}` : '',
    `mapped to ${input.group.dealLabel}`,
    '— issued from Designer invoice review.',
  ]
    .filter(Boolean)
    .join(' ');
  return {
    costObject: buildPurchaseCostObject({
      spendClass: 'job',
      plBucket: input.group.plBucket,
      dealId: input.group.dealId,
      projectId: input.group.projectId,
      jobLabel: input.group.dealLabel,
    }),
    lineItems,
    justification,
    title,
  };
}
